import React, { useEffect, useRef, useState } from "react";
import { center } from "@turf/turf";
import { useTranslation } from "react-i18next";
import useMediaQuery from "../../hooks/useMediaQuery";
import { IMapFilterSearch } from "./map.interfaces";

import { SearchData } from "./hooks/useMapData";

const groupBy = (x: any, f: any) =>
  x.reduce((a: any, b: any, i: any) => ((a[f(b, i, x)] ||= []).push(b), a), {});

const MapFilterSearch = ({
  map,
  searchData,
  customMapLayers,
  isLinerMap,
}: IMapFilterSearch) => {
  const [searchResult, setSearchResult] = useState<SearchData[]>([]);
  const [activeSearchResult, setActiveSearchResult] = useState(0);
  const { t } = useTranslation();
  const searchResultListRef = useRef<HTMLUListElement | null>(null);

  // -------------------------------------------------------
  // SEARCH MAP - when typing in search field -> search
  // -------------------------------------------------------

  const searchMap = (e: React.FormEvent<HTMLInputElement>) => {
    setSearchResult([]);
    setActiveSearchResult(0);


    const searchValue = e.currentTarget.value;
    const primaryResult = searchData.filter((value) =>
      value.title?.toLowerCase().includes(searchValue.toLowerCase())
    );

    const secondaryResult = searchData.filter(
      (value) =>
        value.cmsNodeData?.body
          ?.toLowerCase()
          .includes(searchValue.toLowerCase()) &&
        !primaryResult.some(
          (pr) => pr.cmsNodeData?.nodeId === value.cmsNodeData?.nodeId
        )
    );

    setSearchResult([...primaryResult, ...secondaryResult]);
  };

  const closeSearch = (e: React.FormEvent<HTMLElement>) => {
    // Close the search results if the clicked target isn't a search result
    if (!e.relatedTarget.classList.contains('search-result-item')) {
      setSearchResult([]);
    }
  }

  // Set the height of the search result list when the searchResult is updated
  useEffect(() => {
    if (!searchResultListRef.current) return;

    const canvas = document.querySelector<HTMLDivElement>('.mapboxgl-canvas');
    searchResultListRef.current!.style.maxHeight = `calc(${canvas!.style.height} - 60px)`;
  }, [searchResult])

  // ----------------------------------------------------------------------
  // ON KEY DOWN  - Key presses on search field
  //              - ENTER for search
  //              - Arrow up/down for navigating between search results
  // ----------------------------------------------------------------------

  const onKeyDown = (e: any) => {
    if (searchResult?.length > 0) {
      if (e.keyCode === 38 && activeSearchResult > 0) {
        e.preventDefault();
        setActiveSearchResult(activeSearchResult - 1);
      } else if (
        e.keyCode === 40 &&
        activeSearchResult < searchResult.length - 1
      ) {
        e.preventDefault();
        setActiveSearchResult(activeSearchResult + 1);
      }

      if (e.keyCode === 13) {
        navigateToSearchResult(searchResult[activeSearchResult]);
      }
    }
  };

  // -------------------------------------------------------------
  // NAVIGATE TO SEARCH RESULT - Map flyes to the search result
  // -------------------------------------------------------------

  const navigateToSearchResult = (result: SearchData) => {
    if (result && result.mapboxFeatureData) {
      if (result.mapboxFeatureData.properties)
        result.mapboxFeatureData.properties.selected = true;

      const geometry = result.mapboxFeatureData?.geometry as any;
      const geometryCoordinates = (result.mapboxFeatureData?.geometry as any)
        .coordinates;
      const coordinates =
        typeof geometryCoordinates[0] === "number"
          ? geometryCoordinates
          : center(geometry).geometry.coordinates;

      if (coordinates) {
        const windowCoords = map?.getCenter();

        const distance = Math.sqrt(
          Math.pow(windowCoords ? windowCoords.lng - coordinates[0] : 0, 2) +
            Math.pow(windowCoords ? windowCoords.lat - coordinates[1] : 0, 2)
        );

        map?.flyTo({
          essential: true,
          center: {
            lng: coordinates[0],
            lat: coordinates[1],
          },
          pitch: result.cmsNodeData?.pitch || map.getPitch(),
          bearing: result.cmsNodeData?.bearing || map.getBearing(),
          zoom: isLinerMap ? 3.5 : result.cmsNodeData?.zoom || map.getZoom(),
        });

        setTimeout(() => {
          map?.fire("click", {
            lngLat: {
              lon: coordinates[0],
              lat: coordinates[1],
            },
            point: map.project(coordinates),
            originalEvent: {},
          });
        }, distance * 15 + 250);
      }
    }
    setSearchResult([]);
  };

  // -------------------------------------------------------------
  // JSX
  // -------------------------------------------------------------

  return (
    <div className="map-filter-search">
      <input
        className="ghab-input"
        type="text"
        id="map-search"
        required
        onChange={searchMap}
        onFocus={searchMap}
        onBlur={closeSearch}
        onKeyDown={(e) => onKeyDown(e)}
        name="search"
        placeholder={`${t("map.searchPlaceholder")}`}
      />
      <button
        type="button"
        title={`${t("map.search")}`}
        className="map-filter-search-button"
        aria-label="Submit search query"
        onClick={() => navigateToSearchResult(searchResult[activeSearchResult])}
      >
        <img src="/Assets/icons/search_icon_white.svg" alt="search icon" />
      </button>
      {searchResult.length > 0 ? (
        <div className="search-result-box">
          <div className="search-result-box-content">
            <ul ref={searchResultListRef}>
              {searchResult.map((result, index) => (
                <li key={`${result.title}_${index} `}>
                  <button
                    className={`search-result-item ${
                      activeSearchResult === index ? "active" : ""
                    }`}
                    type="button"
                    onClick={() => navigateToSearchResult(result)}
                  >
                    {result.title}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default MapFilterSearch;
